import TuneIcon from '@mui/icons-material/Tune';
import { Box, Drawer, Typography } from '@mui/material';
import { FC, useState } from 'react';

import CustomButton from 'components/CustomButton';
import GenericConfirmationDialog from 'components/GenericConfirmationDialog';
import AdvancedInventoryFilter from 'containers/Inventory/AdvancedInventoryFilter';
import SimpleInventoryFilter from 'containers/Inventory/SimpleInventoryFilter';
import { useInventoryFilter } from 'context/InventoryFilterContext';

import { InventoryFilterType } from 'constants/enums';
import { getActiveFilters } from 'utils/inventory';

import { StyledAppliedBox, StyledClearAll } from './index.styled';

const InventoryFilterFlyout: FC = () => {
  const {
    filterFlyoutState,
    activeFiltersCount,
    selectedFiltersCount,
    onFiltersClick,
    handleFilterClose,
    handleApplyFilters,
    onClearFiltersClick,
    onSwitchFilterClick,
  } = useInventoryFilter();

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false);

  const showConfirmationDialog = () => {
    setOpenConfirmationDialog(true);
  };

  const hideConfirmationDialog = () => {
    setOpenConfirmationDialog(false);
  };

  const handleSubmit = () => {
    hideConfirmationDialog();
    onSwitchFilterClick();
  };

  const handleSwitchFilter = () => {
    if (activeFiltersCount > 0) {
      showConfirmationDialog();
    } else {
      onSwitchFilterClick();
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={2}>
        {activeFiltersCount !== 0 && (
          <Typography
            fontWeight={700}
            color="textSecondary"
          >
            {getActiveFilters(activeFiltersCount, 'Applied')}
          </Typography>
        )}
        <CustomButton
          icon={<TuneIcon sx={{ transform: 'rotate(90deg)' }} />}
          variant="contained"
          id="button-advancedFilters"
          onClick={onFiltersClick}
        >
          Filters
        </CustomButton>
      </Box>

      {filterFlyoutState.open && (
        <Drawer
          variant="temporary"
          anchor="right"
          open={filterFlyoutState.open}
          onClose={handleFilterClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 600,
                padding: 5,
              },
            },
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={6} mx={2}>
            <Typography fontWeight={700}>
              Filters
            </Typography>
            <CustomButton
              size="small"
              variant="outlined"
              id="button-switchFilterType"
              onClick={handleSwitchFilter}
            >
              {filterFlyoutState.type === InventoryFilterType.SIMPLE ? 'Advanced' : 'Simple'}
            </CustomButton>
            {openConfirmationDialog && (
              <GenericConfirmationDialog
                isOpen={openConfirmationDialog}
                dialogMessage="Are you sure you want to switch filters? The applied ones will be lost!"
                onClose={hideConfirmationDialog}
                onSubmit={handleSubmit}
              />
            )}
          </Box>
          {selectedFiltersCount !== 0 && (
            <StyledAppliedBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography>
                {getActiveFilters(selectedFiltersCount, 'Selected')}
              </Typography>
              <StyledClearAll
                className="pointer"
                onClick={onClearFiltersClick}
              >
                Clear All
              </StyledClearAll>
            </StyledAppliedBox>
          )}

          { filterFlyoutState.type === InventoryFilterType.ADVANCED && <AdvancedInventoryFilter /> }
          { filterFlyoutState.type === InventoryFilterType.SIMPLE && <SimpleInventoryFilter /> }

          <Box display="flex" justifyContent="center" gap={2} mt={3} mx={2}>
            <CustomButton
              size="small"
              id="button-applyFilters"
              variant="outlined"
              color="primary"
              onClick={handleFilterClose}
            >
              Cancel
            </CustomButton>
            <CustomButton
              size="small"
              id="button-applyFilters"
              variant="contained"
              sx={{ width: '100%' }}
              color="primary"
              onClick={handleApplyFilters}
            >
              Apply
            </CustomButton>
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default InventoryFilterFlyout;
